/**
 * Main JavaScript entry point for KWE Sitepackage
 * 
 * This file initializes Bootstrap components and handles
 * navbar-specific functionality for the KWE Ehningen website.
 * 
 * @author KWE Development Team
 * @version 1.0.0
 */

// Import Bootstrap components
import { Dropdown } from 'bootstrap';

// Import navbar functionality
import { initializeDropdowns, initializeCollapseComponent } from './navbar.js';

// Store references to initialized components
let dropdownInstances = [];
let collapseInstance = null;

/**
 * Initialize accessibility enhancements for navbar
 * Ensures proper keyboard navigation and ARIA state management
 */
function initializeAccessibilityEnhancements() {
    // Handle keyboard navigation for dropdowns
    const dropdownToggles = document.querySelectorAll('.dropdown-toggle');
    
    dropdownToggles.forEach(toggle => {
        // Keyboard navigation for dropdown toggles
        toggle.addEventListener('keydown', function(event) {
            const dropdown = Dropdown.getInstance(toggle);
            
            switch (event.key) {
                case 'Enter':
                case ' ':
                    event.preventDefault();
                    toggle.click();
                    break;
                    
                case 'Escape':
                    if (dropdown && toggle.getAttribute('aria-expanded') === 'true') {
                        dropdown.hide();
                        toggle.focus();
                    }
                    break;
                    
                case 'ArrowDown':
                    event.preventDefault();
                    if (dropdown && toggle.getAttribute('aria-expanded') !== 'true') {
                        dropdown.show();
                    }
                    // Focus first dropdown item
                    setTimeout(() => {
                        const firstItem = toggle.nextElementSibling?.querySelector('.dropdown-item');
                        if (firstItem) firstItem.focus();
                    }, 100);
                    break;
            }
        });
        
        // Update ARIA attributes when dropdown state changes
        toggle.addEventListener('shown.bs.dropdown', function() {
            toggle.setAttribute('aria-expanded', 'true');
            // Announce to screen readers
            const dropdownMenu = toggle.nextElementSibling;
            if (dropdownMenu) {
                dropdownMenu.setAttribute('aria-hidden', 'false');
            }
        });
        
        toggle.addEventListener('hidden.bs.dropdown', function() {
            toggle.setAttribute('aria-expanded', 'false');
            // Announce to screen readers
            const dropdownMenu = toggle.nextElementSibling;
            if (dropdownMenu) {
                dropdownMenu.setAttribute('aria-hidden', 'true');
            }
        });
    });
    
    // Handle keyboard navigation within dropdown menus
    const dropdownMenus = document.querySelectorAll('.dropdown-menu');
    dropdownMenus.forEach(menu => {
        const items = menu.querySelectorAll('.dropdown-item');
        
        items.forEach((item, index) => {
            item.addEventListener('keydown', function(event) {
                switch (event.key) {
                    case 'ArrowDown':
                        event.preventDefault();
                        const nextItem = items[index + 1] || items[0];
                        nextItem.focus();
                        break;
                        
                    case 'ArrowUp':
                        event.preventDefault();
                        const prevItem = items[index - 1] || items[items.length - 1];
                        prevItem.focus();
                        break;
                        
                    case 'Escape':
                        event.preventDefault();
                        const escapeToggle = menu.previousElementSibling;
                        if (escapeToggle && escapeToggle.classList.contains('dropdown-toggle')) {
                            const dropdown = Dropdown.getInstance(escapeToggle);
                            if (dropdown) {
                                dropdown.hide();
                                escapeToggle.focus();
                            }
                        }
                        break;
                        
                    case 'Tab':
                        // Allow normal tab behavior but close dropdown
                        const tabToggle = menu.previousElementSibling;
                        if (tabToggle && tabToggle.classList.contains('dropdown-toggle')) {
                            const dropdown = Dropdown.getInstance(tabToggle);
                            if (dropdown) {
                                dropdown.hide();
                            }
                        }
                        break;
                }
            });
        });
    });
    
    // Handle mobile menu accessibility
    const navbarToggler = document.querySelector('.navbar-toggler');
    const navbarCollapse = document.querySelector('#navbarNav');
    
    if (navbarToggler && navbarCollapse) {
        // Update ARIA attributes for mobile menu
        navbarCollapse.addEventListener('shown.bs.collapse', function() {
            navbarToggler.setAttribute('aria-expanded', 'true');
            navbarCollapse.setAttribute('aria-hidden', 'false');
        });
        
        navbarCollapse.addEventListener('hidden.bs.collapse', function() {
            navbarToggler.setAttribute('aria-expanded', 'false');
            navbarCollapse.setAttribute('aria-hidden', 'true');
        });
        
        // Handle keyboard navigation for mobile menu toggle
        navbarToggler.addEventListener('keydown', function(event) {
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                navbarToggler.click();
            }
        });
    }
    
    // Ensure proper focus management for skip links
    const skipLinks = document.querySelectorAll('a[href^="#"]');
    skipLinks.forEach(link => {
        link.addEventListener('click', function(event) {
            const targetId = link.getAttribute('href').substring(1);
            const target = document.getElementById(targetId);
            if (target) {
                // Make target focusable if it's not already
                if (!target.hasAttribute('tabindex')) {
                    target.setAttribute('tabindex', '-1');
                }
                // Focus the target element
                setTimeout(() => {
                    target.focus();
                }, 100);
            }
        });
    });
    
    console.log('Initialized accessibility enhancements');
}

/**
 * Main initialization function
 * Called when DOM is fully loaded
 */
function initializeNavbar() {
    try {
        // Initialize Bootstrap components
        dropdownInstances = initializeDropdowns();
        collapseInstance = initializeCollapseComponent();
        
        // Initialize additional functionality
        initializeAccessibilityEnhancements();
        initializeMobileMenuInteractions();
        
        console.log('KWE Navbar initialization completed successfully');
    } catch (error) {
        console.error('Error initializing navbar components:', error);
    }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeNavbar);
} else {
    // DOM is already loaded
    initializeNavbar();
}

/**
 * Initialize mobile menu interactions
 * Handles mobile menu open/close functionality, click-outside-to-close behavior,
 * and smooth transitions
 */
function initializeMobileMenuInteractions() {
    const navbarToggler = document.querySelector('.navbar-toggler');
    const navbarCollapse = document.querySelector('#navbarNav');
    
    if (!navbarToggler || !navbarCollapse || !collapseInstance) {
        console.log('Mobile menu elements not found or collapse not initialized');
        return;
    }
    
    // Add click outside to close functionality
    document.addEventListener('click', function(event) {
        const isClickInsideNav = navbarCollapse.contains(event.target);
        const isClickOnToggler = navbarToggler.contains(event.target);
        
        if (!isClickInsideNav && !isClickOnToggler && navbarCollapse.classList.contains('show')) {
            collapseInstance.hide();
        }
    });
    
    // Close mobile menu when clicking on nav links (but not dropdown toggles)
    const navLinks = navbarCollapse.querySelectorAll('.nav-link:not(.dropdown-toggle)');
    navLinks.forEach(link => {
        link.addEventListener('click', () => {
            if (navbarCollapse.classList.contains('show')) {
                collapseInstance.hide();
            }
        });
    });
    
    // Close mobile menu when clicking on dropdown items
    const dropdownItems = navbarCollapse.querySelectorAll('.dropdown-item');
    dropdownItems.forEach(item => {
        item.addEventListener('click', () => {
            if (navbarCollapse.classList.contains('show')) {
                // Add a small delay to allow the navigation to complete
                setTimeout(() => {
                    collapseInstance.hide();
                }, 150);
            }
        });
    });
    
    // Enhanced smooth transitions and animations
    navbarCollapse.addEventListener('show.bs.collapse', function() {
        // Add transition styles
        navbarCollapse.style.transition = 'height 0.35s ease-in-out';
        
        // Add opening animation class
        navbarCollapse.classList.add('navbar-opening');
        
        // Animate menu items with stagger effect
        const menuItems = navbarCollapse.querySelectorAll('.nav-item');
        menuItems.forEach((item, index) => {
            item.style.opacity = '0';
            item.style.transform = 'translateY(-10px)';
            item.style.transition = `opacity 0.3s ease ${index * 0.1}s, transform 0.3s ease ${index * 0.1}s`;
            
            setTimeout(() => {
                item.style.opacity = '1';
                item.style.transform = 'translateY(0)';
            }, 100 + (index * 50));
        });
    });
    
    navbarCollapse.addEventListener('shown.bs.collapse', function() {
        // Remove opening class after animation completes
        navbarCollapse.classList.remove('navbar-opening');
        
        // Focus first menu item for accessibility
        const firstMenuItem = navbarCollapse.querySelector('.nav-link');
        if (firstMenuItem) {
            firstMenuItem.focus();
        }
    });
    
    navbarCollapse.addEventListener('hide.bs.collapse', function() {
        // Add transition styles
        navbarCollapse.style.transition = 'height 0.35s ease-in-out';
        
        // Add closing animation class
        navbarCollapse.classList.add('navbar-closing');
        
        // Animate menu items out
        const menuItems = navbarCollapse.querySelectorAll('.nav-item');
        menuItems.forEach((item, index) => {
            item.style.transition = `opacity 0.2s ease ${index * 0.05}s, transform 0.2s ease ${index * 0.05}s`;
            item.style.opacity = '0';
            item.style.transform = 'translateY(-5px)';
        });
    });
    
    navbarCollapse.addEventListener('hidden.bs.collapse', function() {
        // Remove closing class and reset styles
        navbarCollapse.classList.remove('navbar-closing');
        
        // Reset menu item styles
        const menuItems = navbarCollapse.querySelectorAll('.nav-item');
        menuItems.forEach(item => {
            item.style.opacity = '';
            item.style.transform = '';
            item.style.transition = '';
        });
        
        // Return focus to toggle button
        navbarToggler.focus();
    });
    
    // Handle escape key to close mobile menu
    document.addEventListener('keydown', function(event) {
        if (event.key === 'Escape' && navbarCollapse.classList.contains('show')) {
            collapseInstance.hide();
        }
    });
    
    // Handle window resize to close mobile menu if switching to desktop
    let resizeTimer;
    window.addEventListener('resize', function() {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
            // Close mobile menu if window is resized to desktop size
            if (window.innerWidth >= 992 && navbarCollapse.classList.contains('show')) {
                collapseInstance.hide();
            }
        }, 250);
    });
    
    // Add touch gesture support for mobile menu
    let touchStartY = 0;
    let touchEndY = 0;
    
    navbarCollapse.addEventListener('touchstart', function(event) {
        touchStartY = event.changedTouches[0].screenY;
    }, { passive: true });
    
    navbarCollapse.addEventListener('touchend', function(event) {
        touchEndY = event.changedTouches[0].screenY;
        
        // Close menu on upward swipe (swipe up gesture)
        if (touchStartY - touchEndY > 50 && navbarCollapse.classList.contains('show')) {
            collapseInstance.hide();
        }
    }, { passive: true });
    
    console.log('Initialized mobile menu interactions with enhanced animations');
}

// Export functions for potential external use
export {
    initializeAccessibilityEnhancements,
    initializeMobileMenuInteractions,
    initializeNavbar
};